import { useRef, useEffect, useState, useCallback } from 'react';
import './GooeyNav.css';

const GooeyNav = ({
  items,
  animationTime = 600,
  particleCount = 15,
  particleDistances = [90, 10],
  particleR = 100,
  timeVariance = 300,
  colors = [1, 2, 3, 1, 2, 3, 1, 4],
  activeIndex: controlledIndex = 0,
  darkMode = true,
}) => {
  const containerRef = useRef(null);
  const navRef = useRef(null);
  const filterRef = useRef(null);
  const backdropRef = useRef(null);
  const textRef = useRef(null);
  const [internalIndex, setInternalIndex] = useState(controlledIndex);
  const isClickRef = useRef(false);

  const noise = (n = 1) => n / 2 - Math.random() * n;

  const getXY = (distance, pointIndex, totalPoints) => {
    const angle = ((360 + noise(8)) / totalPoints) * pointIndex * (Math.PI / 180);
    return [distance * Math.cos(angle), distance * Math.sin(angle)];
  };

  const createParticle = (i, t, d, r) => {
    let rotate = noise(r / 10);
    return {
      start: getXY(d[0], particleCount - i, particleCount),
      end: getXY(d[1] + noise(7), particleCount - i, particleCount),
      time: t,
      scale: 1 + noise(0.2),
      color: colors[Math.floor(Math.random() * colors.length)],
      rotate: rotate > 0 ? (rotate + r / 20) * 10 : (rotate - r / 20) * 10,
    };
  };

  const makeParticles = useCallback((element) => {
    if (!element) return;
    const d = particleDistances;
    const r = particleR;
    const bubbleTime = animationTime * 2 + timeVariance;
    element.style.setProperty('--time', `${bubbleTime}ms`);

    for (let i = 0; i < particleCount; i++) {
      const t = animationTime * 2 + noise(timeVariance * 2);
      const p = createParticle(i, t, d, r);
      element.classList.remove('active');

      setTimeout(() => {
        if (!element.parentNode) return;
        const particle = document.createElement('span');
        const point = document.createElement('span');
        particle.classList.add('particle');
        particle.style.setProperty('--start-x', `${p.start[0]}px`);
        particle.style.setProperty('--start-y', `${p.start[1]}px`);
        particle.style.setProperty('--end-x', `${p.end[0]}px`);
        particle.style.setProperty('--end-y', `${p.end[1]}px`);
        particle.style.setProperty('--time', `${p.time}ms`);
        particle.style.setProperty('--scale', `${p.scale}`);
        particle.style.setProperty('--color', `var(--color-${p.color}, white)`);
        particle.style.setProperty('--rotate', `${p.rotate}deg`);

        point.classList.add('point');
        particle.appendChild(point);
        element.appendChild(particle);
        requestAnimationFrame(() => {
          element.classList.add('active');
        });
        setTimeout(() => {
          try { element.removeChild(particle); } catch { /* already removed */ }
        }, t);
      }, 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationTime, particleCount, particleDistances, particleR, timeVariance]);

  const updateEffectPosition = useCallback((element) => {
    if (!containerRef.current || !filterRef.current || !textRef.current) return;

    let top = 0;
    let left = 0;
    let el = element;
    while (el && el !== containerRef.current) {
      top += el.offsetTop;
      left += el.offsetLeft;
      el = el.offsetParent;
    }

    const styles = {
      left: `${left}px`,
      top: `${top}px`,
      width: `${element.offsetWidth}px`,
      height: `${element.offsetHeight}px`,
    };
    Object.assign(filterRef.current.style, styles);
    Object.assign(textRef.current.style, styles);
  }, []);

  const handleClick = (e, index) => {
    const liEl = e.currentTarget;
    if (internalIndex === index) return;

    isClickRef.current = true;
    setInternalIndex(index);
    updateEffectPosition(liEl);

    if (filterRef.current) {
      const particles = filterRef.current.querySelectorAll('.particle');
      particles.forEach((p) => { try { filterRef.current.removeChild(p); } catch { /* ok */ } });
    }

    if (textRef.current) {
      textRef.current.classList.remove('active');
      void textRef.current.offsetWidth;
      textRef.current.classList.add('active');
    }

    if (filterRef.current) {
      filterRef.current.classList.add('is-animating');
      makeParticles(filterRef.current);
      const bubbleTime = animationTime * 2 + timeVariance;
      setTimeout(() => {
        if (filterRef.current) filterRef.current.classList.remove('is-animating');
      }, bubbleTime + 100);
    }

    if (items[index]?.onClick) {
      items[index].onClick();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const liEl = e.currentTarget.parentElement;
      if (liEl) handleClick({ currentTarget: liEl }, index);
    }
  };

  useEffect(() => {
    if (controlledIndex !== internalIndex) {
      isClickRef.current = false;
      setInternalIndex(controlledIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledIndex]);

  useEffect(() => {
    if (!navRef.current || !containerRef.current) return;
    const activeLi = navRef.current.querySelectorAll('li')[internalIndex];
    if (activeLi) {
      updateEffectPosition(activeLi);
      if (textRef.current) textRef.current.classList.add('active');
      if (filterRef.current) {
        filterRef.current.classList.add('active');
        filterRef.current.classList.add('is-animating');
        setTimeout(() => {
          if (filterRef.current) filterRef.current.classList.remove('is-animating');
        }, 500); // 300ms gooey-pill animation + buffer
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      const currentActiveLi = navRef.current?.querySelectorAll('li')[internalIndex];
      if (currentActiveLi) updateEffectPosition(currentActiveLi);
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [internalIndex, updateEffectPosition]);

  return (
    <div
      className={`gooey-nav-container ${darkMode ? 'gooey-dark' : 'gooey-light'}`}
      ref={containerRef}
    >
      <nav>
        <ul ref={navRef}>
          {items.map((item, index) => (
            <li
              key={index}
              className={internalIndex === index ? 'active' : ''}
              onClick={(e) => handleClick(e, index)}
            >
              <a
                href={item.href || '#'}
                onClick={(e) => e.preventDefault()}
                onKeyDown={(e) => handleKeyDown(e, index)}
              >
                {item.icon && <span className="gooey-nav-icon">{item.icon}</span>}
                <span className="gooey-nav-label">{item.label}</span>
              </a>
            </li>
          ))}
        </ul>
      </nav>
      {darkMode && (
        <span
          ref={backdropRef}
          className="effect-filter-backdrop"
          aria-hidden
        />
      )}
      <span className="effect filter" ref={filterRef} />
      <span className="effect text" ref={textRef} />
    </div>
  );
};

export default GooeyNav;
